import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputBase,
    MenuItem,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { getMRTDefaultProps } from '../../../../../../helpers/mrt/getMRTDefaultProps';

const formatarQuantidade = (value) =>
    Number(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    });
const formatarContagem = (value) => {
    const texto = String(value ?? '').trim();

    if (texto === '') return '';

    const numero = Number(texto.replace(',', '.'));

    if (!Number.isFinite(numero)) return texto;

    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    });
};
const CATEGORIAS = [
    { value: 'produto', label: 'Produtos' },
    { value: 'isBobina', label: 'Bobinas' },
    { value: 'bem', label: 'Bens' },
    { value: 'imovel', label: 'Imóveis' },
    { value: 'vtr', label: 'Viaturas' },
];

const COLUNA_CENTRALIZADA = {
    muiTableHeadCellProps: {
        align: 'center',
    },
    muiTableBodyCellProps: {
        align: 'center',
    },
};

const COLUNA_NUMERICA = {
    muiTableHeadCellProps: {
        align: 'right',
    },
    muiTableBodyCellProps: {
        align: 'right',
        sx: {
            fontVariantNumeric: 'tabular-nums',
        },
    },
};

const COLUNA_TEXTO = {
    muiTableHeadCellProps: {
        align: 'left',
    },
    muiTableBodyCellProps: {
        align: 'left',
    },
};

function ContagemInput({ item, value, onChange, disabled }) {
    const [focused, setFocused] = useState(false);
    const possuiValor = value !== '';

    return (
        <InputBase
            value={focused ? value : formatarContagem(value)}
            onChange={(event) => onChange(event.target.value)}
            onFocus={(event) => {
                setFocused(true);

                requestAnimationFrame(() => {
                    event.target.select();
                });
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }}
            disabled={disabled}
            placeholder={disabled ? '' : 'Digite'}
            inputProps={{
                inputMode: 'decimal',
                'aria-label': `Contagem do item ${item?.ordemBalanco || ''}`,
            }}
            sx={{
                width: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: disabled ? 'action.disabledBackground' : possuiValor ? 'background.paper' : 'action.hover',
                transition: (muiTheme) => muiTheme.transitions.create(['border-color', 'background-color', 'box-shadow']),
                '&:hover': disabled
                    ? undefined
                    : {
                          borderColor: 'text.secondary',
                      },
                '&.Mui-focused': {
                    borderColor: 'primary.main',
                    backgroundColor: 'background.paper',
                    boxShadow: (muiTheme) => `0 0 0 1px ${muiTheme.palette.primary.main}`,
                },
                '& .MuiInputBase-input': {
                    padding: '5px 8px',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                },
                '& .MuiInputBase-input::placeholder': {
                    opacity: 0.65,
                    fontStyle: 'italic',
                    textAlign: 'center',
                },
            }}
        />
    );
}

function PessoaBalancoInput({ item, field, label, pessoa, options, onSearch, onSelect, disabled, isLoading }) {
    const [focused, setFocused] = useState(false);
    const [texto, setTexto] = useState(pessoa?.descricao || '');
    const onSearchRef = useRef(onSearch);

    const identificador = item?.uuid || item?.id;
    const datalistId = `balanco-${field}-${identificador}`;

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    useEffect(() => {
        if (!focused) {
            setTexto(pessoa?.descricao || '');
        }
    }, [pessoa?.id, pessoa?.descricao, focused]);

    useEffect(() => {
        if (!focused) return undefined;

        const timeoutId = setTimeout(() => {
            onSearchRef.current?.(texto);
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [texto, focused]);

    const selecionarOpcao = (value) => {
        const selecionada = options.find((option) => option.valorSelecao === value);

        if (!selecionada) return false;

        onSelect(selecionada);
        setTexto(selecionada.descricao || '');

        return true;
    };

    return (
        <>
            <InputBase
                value={texto}
                onChange={(event) => {
                    const value = event.target.value;

                    setTexto(value);
                    selecionarOpcao(value);
                }}
                onFocus={(event) => {
                    setFocused(true);

                    requestAnimationFrame(() => {
                        event.target.select();
                    });
                }}
                onBlur={() => {
                    setFocused(false);

                    if (texto === (pessoa?.descricao || '')) return;

                    if (!selecionarOpcao(texto)) {
                        setTexto(pessoa?.descricao || '');
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        selecionarOpcao(texto);
                    }

                    if (event.key === 'Escape') {
                        event.preventDefault();
                        setTexto(pessoa?.descricao || '');
                        event.currentTarget.blur();
                    }
                }}
                disabled={disabled}
                placeholder={`Pesquisar ${String(label || 'pessoa').toLowerCase()}`}
                endAdornment={isLoading ? <CircularProgress size={16} sx={{ marginRight: 0.5 }} /> : null}
                inputProps={{
                    list: datalistId,
                    autoComplete: 'off',
                    'aria-label': `${label} do item ${item?.ordemBalanco || ''}`,
                }}
                sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
                    '&:hover': disabled
                        ? undefined
                        : {
                              borderColor: 'text.secondary',
                          },
                    '&.Mui-focused': {
                        borderColor: 'primary.main',
                        boxShadow: (muiTheme) => `0 0 0 1px ${muiTheme.palette.primary.main}`,
                    },
                    '& .MuiInputBase-input': {
                        padding: '5px 8px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        opacity: 0.65,
                        fontStyle: 'italic',
                    },
                }}
            />

            {focused && (
                <datalist id={datalistId}>
                    {options.map((option) => (
                        <option key={option.id} value={option.valorSelecao} label={option.label || option.descricao} />
                    ))}
                </datalist>
            )}
        </>
    );
}
export default function BalancoTabPanel({ theme, module, header, config }) {
    const dados = header?.dados || {};
    const parametro = config?.parametro || {};
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const OptionsAlmoxarifados = header?.OptionsAlmoxarifados || [];
    const operacao = config?.operacao || {};

    const configuracao = module?.state?.configuracao || {};
    const changeConfiguration = module?.actions?.changeConfiguration || (() => {});

    const peopleOptions = module?.lookup?.people?.options || [];
    const isPeopleLoading = module?.lookup?.people?.flags?.isLoading ?? false;

    const searchPeople = module?.actions?.searchPeople || (() => {});
    const changeCounter = module?.actions?.changeCounter || (() => {});
    const changeChecker = module?.actions?.changeChecker || (() => {});
    const [previewPagination, setPreviewPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [rowSelection, setRowSelection] = useState({});
    const [openApplyDialog, setOpenApplyDialog] = useState(false);

    const saveCounts = module?.actions?.saveCounts || (() => {});
    const conferirItens = module?.actions?.conferirItens || (() => {});
    const aplicarBalanco = module?.actions?.aplicarBalanco || (() => {});
    const totalContagens = Number(module?.draft?.totalContagens || 0);
    const totalAlterados = Number(module?.draft?.totalAlterados || 0);
    const isSavingCounts = module?.flags?.isSavingCounts ?? false;
    const isCheckingItems = module?.flags?.isCheckingItems ?? false;
    const isApplyingBalance = module?.flags?.isApplyingBalance ?? false;

    const consultarProdutos = async () => {
        setPreviewPagination((current) => ({
            ...current,
            pageIndex: 0,
        }));

        return module?.actions?.preview?.();
    };

    const previewItems = module?.preview?.items || [];

    const previewMeta = module?.preview?.meta || {};

    const isPreviewLoading = module?.preview?.flags?.isLoading ?? false;

    const isPreviewError = module?.preview?.flags?.isError ?? false;

    const items = module?.list?.items || [];
    const rowCount = module?.list?.rowCount || 0;
    const meta = module?.state?.meta || {};
    const estado = module?.state?.estado || {};
    const contagensEditadas = module?.draft?.contagens || {};
    const changeCount = module?.actions?.changeCount || (() => {});

    const temPermissaoEditarItem = typeof config?.podeEditarItem === 'function' && config.podeEditarItem();

    const podeDigitarContagem = temPermissaoEditarItem && estado.podeEditarContagem === true;
    const mrt = module?.mrt || {};

    const pagination = mrt?.state?.pagination || {
        pageIndex: 0,
        pageSize: 50,
    };

    const setPagination = mrt?.setters?.setPagination || (() => {});

    const isLoading = mrt?.flags?.isLoading ?? false;
    const isRefetching = mrt?.flags?.isRefetching ?? false;
    const isError = mrt?.flags?.isError ?? false;
    const podeSelecionarConferencia =
        temPermissaoEditarItem &&
        estado.podeConferir === true &&
        !isLoading &&
        !isRefetching &&
        !isSavingCounts &&
        !isCheckingItems &&
        !isApplyingBalance;
    const podeAplicarBalanco =
        temPermissaoEditarItem &&
        estado.podeAplicar === true &&
        totalAlterados === 0 &&
        !isLoading &&
        !isRefetching &&
        !isSavingCounts &&
        !isCheckingItems &&
        !isApplyingBalance;

    useEffect(() => {
        setRowSelection({});
    }, [dados?.id, pagination.pageIndex, pagination.pageSize]);

    const itensSelecionados = useMemo(
        () => items.filter((item) => rowSelection[String(item?.uuid || item?.id)] === true),
        [items, rowSelection]
    );

    const itemPossuiAlteracaoConferencia = (item) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;
        const draft = chave ? contagensEditadas[chave] : null;

        return Boolean(
            draft &&
            (Object.prototype.hasOwnProperty.call(draft, 'quantidade') ||
                Object.prototype.hasOwnProperty.call(draft, 'colaboradorId') ||
                Object.prototype.hasOwnProperty.call(draft, 'observacao'))
        );
    };

    const handleConferirSelecionados = async () => {
        const resultado = await conferirItens(itensSelecionados);

        if (resultado) {
            setRowSelection({});
        }
    };

    const handleCloseApplyDialog = () => {
        if (isApplyingBalance) return;

        setOpenApplyDialog(false);
    };

    const handleConfirmApplyBalance = async () => {
        const resultado = await aplicarBalanco();

        if (resultado) {
            setRowSelection({});
            setOpenApplyDialog(false);
        }
    };

    const obterValorContagem = (item) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

        if (chave && Object.prototype.hasOwnProperty.call(contagensEditadas, chave)) {
            return contagensEditadas[chave]?.quantidade ?? '';
        }

        if (item?.contado === true) {
            return String(item?.quantidade ?? '');
        }

        return '';
    };

    const obterPessoaItem = (item, field) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

        const rascunho = chave ? contagensEditadas[chave] : null;

        if (rascunho && Object.prototype.hasOwnProperty.call(rascunho, field)) {
            return rascunho[field];
        }

        return item?.[field] || null;
    };
    const noop = () => {};

    const mrtAdapter = {
        columnFilterFns: {},
        columnFilters: [],
        globalFilter: '',
        sorting: [],

        pagination,
        rowCount,

        isLoading,
        isError,
        isRefetching,

        setColumnFilters: noop,
        setColumnFiltersMode: noop,
        setGlobalFilter: noop,
        setSorting: noop,
        setPagination,
    };

    const mrtDefaultProps = getMRTDefaultProps({
        mrt: mrtAdapter,
        parametro,
        isMobile,
    });
    const almoxarifadoSelecionado = OptionsAlmoxarifados.find(
        (item) => Number(item?.id ?? item?.value) === Number(dados?.almoxarifadoId)
    );

    const descricaoAlmoxarifado =
        almoxarifadoSelecionado?.descricao ||
        almoxarifadoSelecionado?.label ||
        dados?.Almoxarifado?.descricao ||
        dados?.almoxarifadoId ||
        '';

    const columns = useMemo(
        () => [
            {
                accessorKey: 'ordemBalanco',
                header: 'Ordem',
                size: 75,
                minSize: 75,
                maxSize: 75,
                ...COLUNA_CENTRALIZADA,
            },
            {
                id: 'codigo',
                header: 'Código',
                accessorFn: (row) => row.Produto?.codigo || '',
                size: 110,
                minSize: 90,
                maxSize: 130,
                ...COLUNA_CENTRALIZADA,
            },
            {
                id: 'produto',
                header: 'Produto',
                accessorFn: (row) => row.Produto?.descricao || '',
                size: 360,
                minSize: 260,
                ...COLUNA_TEXTO,
            },
            {
                id: 'embalagem',
                header: 'Embalagem',
                accessorFn: (row) => row.Embalagem?.descricao || '',
                size: 140,
                minSize: 110,
                maxSize: 180,
                ...COLUNA_CENTRALIZADA,
            },
            {
                accessorKey: 'quantidadeOriginal',
                header: 'Sistema',
                accessorFn: (row) => formatarQuantidade(row.quantidadeOriginal),
                size: 120,
                minSize: 110,
                maxSize: 140,
                ...COLUNA_NUMERICA,
            },
            {
                accessorKey: 'quantidade',
                header: 'Contagem',
                size: 130,
                minSize: 120,
                maxSize: 150,
                ...COLUNA_NUMERICA,
                Cell: ({ row }) => {
                    const item = row.original;

                    return (
                        <ContagemInput
                            item={item}
                            value={obterValorContagem(item)}
                            onChange={(value) => changeCount(item, value)}
                            disabled={
                                !podeDigitarContagem ||
                                isLoading ||
                                isRefetching ||
                                isSavingCounts ||
                                isCheckingItems ||
                                isApplyingBalance
                            }
                        />
                    );
                },
            },
            {
                id: 'situacao',
                header: 'Situação',
                accessorFn: (row) => row.Situacao?.descricao || '',
                size: 170,
                minSize: 140,
                maxSize: 210,
                ...COLUNA_CENTRALIZADA,
            },
            {
                id: 'contador',
                header: 'Contador',
                accessorFn: (row) => row.Pessoa?.descricao || '',
                size: 230,
                minSize: 200,
                ...COLUNA_TEXTO,
                Cell: ({ row }) => {
                    const item = row.original;

                    return (
                        <PessoaBalancoInput
                            item={item}
                            field="contador"
                            label="Contador"
                            pessoa={obterPessoaItem(item, 'Pessoa')}
                            options={peopleOptions}
                            onSearch={searchPeople}
                            onSelect={(pessoa) => changeCounter(item, pessoa)}
                            disabled={
                                !podeDigitarContagem ||
                                isLoading ||
                                isRefetching ||
                                isSavingCounts ||
                                isCheckingItems ||
                                isApplyingBalance
                            }
                            isLoading={isPeopleLoading}
                        />
                    );
                },
            },
            {
                id: 'conferente',
                header: 'Conferente',
                accessorFn: (row) => row.Colaborador?.descricao || '',
                size: 230,
                minSize: 200,
                ...COLUNA_TEXTO,
                Cell: ({ row }) => {
                    const item = row.original;

                    return (
                        <PessoaBalancoInput
                            item={item}
                            field="conferente"
                            label="Conferente"
                            pessoa={obterPessoaItem(item, 'Colaborador')}
                            options={peopleOptions}
                            onSearch={searchPeople}
                            onSelect={(pessoa) => changeChecker(item, pessoa)}
                            disabled={
                                !podeDigitarContagem ||
                                isLoading ||
                                isRefetching ||
                                isSavingCounts ||
                                isCheckingItems ||
                                isApplyingBalance
                            }
                            isLoading={isPeopleLoading}
                        />
                    );
                },
            },
        ],
        [
            contagensEditadas,
            changeCount,
            podeDigitarContagem,
            isLoading,
            isRefetching,
            peopleOptions,
            isPeopleLoading,
            searchPeople,
            changeCounter,
            changeChecker,
            isSavingCounts,
            isCheckingItems,
            isApplyingBalance,
        ]
    );
    const previewColumns = useMemo(
        () => [
            {
                accessorKey: 'ordemBalanco',
                header: 'Ordem',
                size: 75,
                minSize: 75,
                maxSize: 75,
                ...COLUNA_CENTRALIZADA,
            },
            {
                accessorKey: 'codigo',
                header: 'Código',
                size: 110,
                minSize: 90,
                maxSize: 130,
                ...COLUNA_CENTRALIZADA,
            },
            {
                accessorKey: 'descricao',
                header: 'Produto',
                size: 400,
                minSize: 280,
                ...COLUNA_TEXTO,
            },
            {
                id: 'saldo',
                header: 'Saldo',
                accessorFn: (row) => formatarQuantidade(row.saldo),
                size: 120,
                minSize: 110,
                maxSize: 140,
                ...COLUNA_NUMERICA,
            },
            {
                id: 'fator',
                header: 'Fator',
                accessorFn: (row) => formatarQuantidade(row.fator || 1),
                size: 110,
                minSize: 100,
                maxSize: 130,
                ...COLUNA_NUMERICA,
            },
        ],
        []
    );
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ margin: '1em' }}>
                <Typography variant="h6">Balanço de estoque</Typography>

                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                    {dados?.id
                        ? `Movimento nº ${dados?.numero || dados.id} — ${Number(meta.totalContados || 0)} contado(s) de ${Number(
                              meta.totalRowCount || 0
                          )} item(ns).`
                        : `Configure os dados para abrir um novo ${operacao?.descricao || 'balanço'}.`}
                </Typography>

                {!dados?.id && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap',
                            marginBottom: 2,
                        }}
                    >
                        <TextField
                            label="Almoxarifado"
                            value={descricaoAlmoxarifado}
                            disabled
                            size="small"
                            sx={{ minWidth: 220 }}
                            helperText="Selecionado no cabeçalho"
                        />

                        <TextField
                            select
                            label="Categoria"
                            value={configuracao?.categoria || 'produto'}
                            onChange={(event) => changeConfiguration('categoria', event.target.value)}
                            size="small"
                            sx={{ minWidth: 180 }}
                        >
                            {CATEGORIAS.map((categoria) => (
                                <MenuItem key={categoria.value} value={categoria.value}>
                                    {categoria.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Descrição começa com"
                            value={configuracao?.descricao || ''}
                            onChange={(event) => changeConfiguration('descricao', event.target.value)}
                            size="small"
                            sx={{ minWidth: 280 }}
                            helperText="Deixe vazio para incluir toda a categoria"
                        />
                        <Button
                            type="button"
                            variant="contained"
                            onClick={consultarProdutos}
                            disabled={!dados?.almoxarifadoId || isPreviewLoading}
                            sx={{
                                height: '40px',
                            }}
                        >
                            {isPreviewLoading
                                ? 'Consultando...'
                                : previewItems.length > 0
                                  ? 'Refazer consulta'
                                  : 'Consultar produtos'}
                        </Button>
                    </Box>
                )}

                {!dados?.id && previewItems.length > 0 && (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                            {Number(previewMeta.totalRowCount || previewItems.length)} produto(s) encontrado(s)
                        </Typography>

                        <MaterialReactTable
                            {...mrtDefaultProps}
                            columns={previewColumns}
                            data={previewItems}
                            getRowId={(row) => String(row.uuid || row.id)}
                            // A prévia recebe todos os produtos e pagina localmente
                            manualPagination={false}
                            manualFiltering={false}
                            manualSorting={false}
                            rowCount={previewItems.length}
                            onPaginationChange={setPreviewPagination}
                            autoResetPageIndex
                            // Grid somente para consulta
                            enableColumnActions={false}
                            enableColumnFilterModes={false}
                            enableColumnFilters={false}
                            enableGlobalFilter={false}
                            enableGlobalFilterModes={false}
                            enableSorting={false}
                            enableColumnOrdering={false}
                            enableColumnPinning={false}
                            enableFacetedValues={false}
                            enableRowActions={false}
                            enableRowSelection={false}
                            enableEditing={false}
                            enableTopToolbar={false}
                            localization={MRT_Localization_PT_BR}
                            muiToolbarAlertBannerProps={
                                isPreviewError
                                    ? {
                                          color: 'error',
                                          children: 'Erro ao consultar os produtos.',
                                      }
                                    : undefined
                            }
                            initialState={{
                                density: 'compact',
                            }}
                            state={{
                                pagination: previewPagination,
                                isLoading: isPreviewLoading,
                                showAlertBanner: isPreviewError,
                                showProgressBars: isPreviewLoading,
                            }}
                        />
                    </Box>
                )}
                {!!dados?.id && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 1,
                                marginBottom: 1,
                            }}
                        >
                            {totalContagens > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {totalContagens} contagem(ns) pendente(s)
                                </Typography>
                            )}

                            <Button
                                type="button"
                                variant="contained"
                                onClick={saveCounts}
                                disabled={
                                    !podeDigitarContagem ||
                                    totalContagens === 0 ||
                                    isSavingCounts ||
                                    isCheckingItems ||
                                    isApplyingBalance ||
                                    isLoading ||
                                    isRefetching
                                }
                            >
                                {isSavingCounts
                                    ? 'Salvando...'
                                    : `Salvar contagens${totalContagens > 0 ? ` (${totalContagens})` : ''}`}
                            </Button>

                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleConferirSelecionados}
                                disabled={!podeSelecionarConferencia || itensSelecionados.length === 0}
                            >
                                {isCheckingItems
                                    ? 'Conferindo...'
                                    : `Conferir selecionados${
                                          itensSelecionados.length > 0 ? ` (${itensSelecionados.length})` : ''
                                      }`}
                            </Button>

                            {(estado.conferenciaConcluida === true || estado.aplicado === true) && (
                                <Button
                                    type="button"
                                    color="error"
                                    variant="contained"
                                    onClick={() => setOpenApplyDialog(true)}
                                    disabled={estado.aplicado === true || !podeAplicarBalanco}
                                >
                                    {estado.aplicado === true
                                        ? 'Balanço aplicado'
                                        : isApplyingBalance
                                          ? 'Aplicando...'
                                          : 'Aplicar balanço'}
                                </Button>
                            )}
                        </Box>
                        <MaterialReactTable
                            {...mrtDefaultProps}
                            columns={columns}
                            data={items}
                            getRowId={(row) => String(row.uuid || row.id)}
                            rowCount={rowCount}
                            manualPagination
                            enableColumnFilterModes={false}
                            enableColumnFilters={false}
                            enableGlobalFilter={false}
                            enableGlobalFilterModes={false}
                            enableSorting={false}
                            enableColumnOrdering={false}
                            enableFacetedValues={false}
                            enableRowActions={false}
                            enableRowSelection={
                                podeSelecionarConferencia
                                    ? (row) => row.original?.conferido !== true || itemPossuiAlteracaoConferencia(row.original)
                                    : false
                            }
                            enableSelectAll={podeSelecionarConferencia}
                            selectAllMode="page"
                            enableEditing={false}
                            manualFiltering={false}
                            manualSorting={false}
                            onPaginationChange={setPagination}
                            onRowSelectionChange={setRowSelection}
                            initialState={{
                                ...mrtDefaultProps.initialState,
                                showColumnFilters: false,
                                density: 'compact',
                            }}
                            state={{
                                ...mrtDefaultProps.state,
                                pagination,
                                rowSelection,
                                isLoading,
                                showAlertBanner: isError,
                                showProgressBars: isRefetching,
                            }}
                        />
                    </>
                )}

                <Dialog
                    open={openApplyDialog}
                    onClose={handleCloseApplyDialog}
                    disableEscapeKeyDown={isApplyingBalance}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Aplicar balanço de estoque</DialogTitle>

                    <DialogContent dividers>
                        <Typography variant="body1" sx={{ marginBottom: 1.5 }}>
                            Esta ação aplicará ao estoque as diferenças encontradas nos {Number(meta.totalRowCount || 0)}
                            itens deste balanço.
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.5 }}>
                            Serão criadas movimentações de entrada ou saída somente para os produtos cuja contagem seja diferente
                            do saldo original.
                        </Typography>

                        <Typography variant="body1" color="error" sx={{ fontWeight: 700 }}>
                            Depois de aplicado, o balanço será fechado e não poderá ser editado, conferido, excluído ou aplicado
                            novamente.
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button type="button" onClick={handleCloseApplyDialog} disabled={isApplyingBalance}>
                            Voltar
                        </Button>

                        <Button
                            type="button"
                            color="error"
                            variant="contained"
                            onClick={handleConfirmApplyBalance}
                            disabled={!podeAplicarBalanco || isApplyingBalance}
                        >
                            {isApplyingBalance ? 'Aplicando...' : 'Aplicar definitivamente'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}
